import { Component } from '@theme/component';

/**
 * Mobile accordion of test-ablauf. mna-v2's open band renders a plain heading
 * without the toggle button, so nothing calls `togglePanel` there.
 */
class TestAblaufComponent extends Component {
  /**
   * @param {MouseEvent} event - Click on the heading's toggle button.
   */
  togglePanel(event) {
    const next = this.dataset.expanded === 'false';
    this.dataset.expanded = String(next);

    if (event.target instanceof HTMLButtonElement) {
      event.target.setAttribute('aria-expanded', String(next));
    }
  }
}

if (!customElements.get('test-ablauf-component')) {
  customElements.define('test-ablauf-component', TestAblaufComponent);
}

/**
 * Turns a YouTube or Vimeo page/share URL into its embed URL.
 *
 * @param {string} raw
 * @returns {string | null} null for anything that isn't a recognised video URL.
 */
function buildEmbedUrl(raw) {
  if (!raw) return null;

  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\.|^m\./, '');

  if (host === 'youtu.be') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
  }

  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    let id = url.searchParams.get('v');
    if (!id) {
      const match = url.pathname.match(/\/(?:embed|shorts|v)\/([^/?#]+)/);
      if (match) id = match[1];
    }
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
  }

  if (host === 'vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean).pop();
    return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
  }

  if (host === 'player.vimeo.com') {
    return raw;
  }

  return null;
}

/**
 * Video modal body. The iframe is created on `dialog:open` and removed on
 * `dialog:close`, so the player only loads on demand and playback stops when
 * the modal closes.
 *
 * @typedef {Object} VideoRefs
 * @property {HTMLElement} frame
 *
 * @extends {Component<VideoRefs>}
 */
class TestAblaufVideo extends Component {
  requiredRefs = ['frame'];

  /** @type {HTMLElement | null} */
  #dialog = null;

  connectedCallback() {
    super.connectedCallback();
    this.#dialog = this.closest('dialog-component');
    if (!this.#dialog) return;
    this.#dialog.addEventListener('dialog:open', this.#mount);
    this.#dialog.addEventListener('dialog:close', this.#unmount);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#dialog) return;
    this.#dialog.removeEventListener('dialog:open', this.#mount);
    this.#dialog.removeEventListener('dialog:close', this.#unmount);
  }

  #mount = () => {
    const embed = buildEmbedUrl(this.dataset.videoUrl ?? '');
    if (!embed) return;

    const iframe = document.createElement('iframe');
    iframe.src = embed;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.title = this.dataset.videoTitle ?? '';
    this.refs.frame.replaceChildren(iframe);
  };

  #unmount = () => {
    this.refs.frame.replaceChildren();
  };
}

if (!customElements.get('test-ablauf-video')) {
  customElements.define('test-ablauf-video', TestAblaufVideo);
}
